<template>
  <div class="tickets-table-wrapper">
    <v-data-table
      ref="ticketsTable"
      :headers="headers"
      :items="items"
      :options.sync="options"
      dense
      :loading="loading"
      loading-text="Please wait..."
      calculate-widths
      item-key="id"
      :mobile-breakpoint="0"
      :server-items-length="total"
      :footer-props="{
        'items-per-page-options': [50, 100],
      }"
      :height="height"
    >
      <template #item.subject="{ item }">
        <EReadMore :content="item.subject" :max-length="45" />
        <OpenInNewWindowButton
          :target-link="`${item.webUrl}`"
          hint="Open In Zoho Desk"
        />
      </template>
      <template #item.statusType="{ item }">
        <span :class="getStatusColor(item.statusType)">{{
          item.statusType
        }}</span>
      </template>
      <template #item.owner="{ item }">
        <div class="e-flex e-flex-col">
          <span class="caption font-weight-medium">{{
            getFullName(item.contact)
          }}</span>
          <span class="caption">{{ getEmail(item.contact) }}</span>
        </div>
      </template>
      <template #item.createdTime="{ item }">
        <span>{{ formatDate(item.createdTime) }}</span>
      </template>
      <template #item.assignee="{ item }">
        <div class="e-flex e-flex-col">
          <span class="caption font-weight-medium">{{
            getFullName(item.assignee)
          }}</span>
          <span class="caption">{{ getEmail(item.assignee) }}</span>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { AdminApi } from "@evercam/shared/api/adminApi"
import { mapStores } from "pinia"
import { useCameraDialogStore } from "@/stores/cameraDialog"
import OpenInNewWindowButton from "@/components/OpenInNewWindowButton"
import { ZohoDeskTicketStatus } from "@evercam/shared/types"

export default {
  components: { OpenInNewWindowButton },
  props: {
    camerasExids: {
      type: Array,
      default: () => [],
    },
    projectExid: {
      type: String,
      default: "",
    },
    userEmail: {
      type: String,
      default: "",
    },
    companyName: {
      type: String,
      default: "",
    },
    height: {
      type: [Number, undefined],
      default: undefined,
    },
  },
  data() {
    return {
      loading: false,
      items: [],
      options: {
        page: 1,
        itemsPerPage: 50,
      },
      total: 0,
      statusColors: {
        [ZohoDeskTicketStatus.Open]: "green--text text--darken-2",
        [ZohoDeskTicketStatus.Closed]: "red--text text--darken-2",
        [ZohoDeskTicketStatus.OnHold]: "deep-orange--text text--darken-2",
      },
    }
  },
  computed: {
    ...mapStores(useCameraDialogStore),
    headers() {
      return [
        {
          value: "ticketNumber",
          text: "ID",
          sortable: false,
        },
        {
          value: "subject",
          text: "Name",
          sortable: false,
        },
        {
          value: "statusType",
          text: "Status",
          sortable: false,
        },
        {
          value: "owner",
          text: "Owner",
          sortable: false,
        },
        {
          value: "createdTime",
          text: "Created At",
          sortable: false,
        },
        {
          value: "assignee",
          text: "Assigned Agent",
          sortable: false,
        },
      ]
    },
  },
  watch: {
    options: {
      handler: "fetchTickets",
      deep: true,
    },
    camerasExids: {
      handler: "fetchTickets",
      deep: true,
    },
    projectExid: {
      handler: "fetchTickets",
      deep: true,
    },
    userEmail: {
      handler: "fetchTickets",
      deep: true,
    },
    companyName: {
      handler: "fetchTickets",
      deep: true,
    },
  },
  mounted() {
    this.fetchTickets()
  },
  methods: {
    async fetchTickets() {
      try {
        let { page, itemsPerPage } = this.options
        this.loading = true
        const payload = {
          ...this.buildFilterPayload(),
          sortBy: "-createdTime",
          limit: itemsPerPage,
          from: (page - 1) * itemsPerPage,
        }
        const { data, count } = await AdminApi.zohoDesk.searchTickets(payload)
        this.total = count

        this.items = data
      } catch (error) {
        this.$notifications.error({
          text: "Failed to fetch tickets!",
          error,
        })
      } finally {
        this.loading = false
      }
    },
    getFullName(item) {
      return item
        ? `${item?.firstName || ""} ${item?.lastName || ""}`.trim()
        : "-"
    },
    getEmail(item) {
      return item?.email || item?.emailId
    },
    getStatusColor(status) {
      return this.statusColors[status] || ""
    },
    buildFilterPayload() {
      const cameraExids = this.camerasExids.filter(Boolean)
      const hasCameras = cameraExids.length > 0

      const filters = {
        projectExid: this.projectExid,
        userEmail: this.userEmail,
        companyName: this.companyName,
        cameras: hasCameras ? cameraExids : null,
      }

      if (filters.projectExid) {
        return { customField1: `Project ID:${filters.projectExid}` }
      }

      if (filters.userEmail) {
        return { email: filters.userEmail }
      }

      if (filters.companyName) {
        return { accountName: filters.companyName }
      }

      if (filters.cameras) {
        const joinedCameras = filters.cameras.map((id) => `${id}*`).join(",")

        return { customField1: `Camera ID:${joinedCameras}` }
      }

      return {}
    },
  },
}
</script>

<style lang="scss">
.tickets-table-wrapper {
  .v-data-table--dense > .v-data-table__wrapper > table {
    td,
    th {
      height: 20px !important;
      font-size: 12px !important;
      color: rgb(75, 85, 99) !important;
      border: none !important;
    }
    & > thead {
      tr {
        background-color: rgba(226, 228, 229, 0.71) !important;
      }
      &:after {
        content: "-";
        display: block;
        line-height: 6px;
        color: transparent;
      }
      th:first-of-type {
        overflow: hidden;
      }
      th:last-of-type {
        overflow: hidden;
      }
    }
    tr:nth-child(odd) {
      background-color: rgba(240, 242, 243, 0.71);
      overflow: hidden;
    }

    & > tbody {
      tr:first-child {
        td:first-child {
          border-top-left-radius: 0.5em;
        }
        td:last-child {
          border-top-right-radius: 0.5em;
        }
      }
      tr:last-child {
        td:first-child {
          border-bottom-left-radius: 0.5em;
        }
        td:last-child {
          border-bottom-right-radius: 0.5em;
        }
      }
    }
  }
}
</style>
